/**
 * @memberof conferenceClient
 * @ngdoc controller
 * @name conferenceCtrl
 * @scope
 * @description
 *   Main controller conference client
 * @param $rootScope {service}
 *   Every application has a single root scope. All other scopes are descendant scopes of the root scope.
 * @param $scope {service}
 *   Controller scope
 * @param socketService {service}
 *   Service for communicating with socket
 * @param vertoService {service}
 *   Service for handling calls to Freeswitch
 * @param attendeeService {service}
 *   Service for maintaining current attendee information
 * @param storageService {service}
 *   Service for communicating with browser localStorage
 * @attribute {string} name
 *   Value based on call to attendeeService.getScreenName()
 * @attribute {string} email
 *   Value based on call to attendeeService.getEmail()
 * @attribute {string} attendantId
 *   Value based on call to attendeeService.getAttendantId()
 * @attribute {boolean} isViewerOnly
 *   Value based on call to attendeeService.getIsViewerOnly()
 * @attribute {boolean} isHost
 *   Value based on call to attendeeService.getIsHost()
 * @attribute {string} audioDevice
 *   Value based on storageService.get('audio-device')
 * @attribute {string} videoDevice
 *   Value based on storageService.get('video-device')
 * @attribute {boolean} audioMuted
 *   Updates when controller receives events that indicate current user has been muted
 * @attribute {boolean} allAudioMuted
 *   Updates when controller receives events that indicate all users have been muted
 * @attribute {function} toggleAudio
 *   Makes toggleAudio() publicly accessible
 * @attribute {function} muteAll
 *   Makes muteAll() publicly accessible
 * @attribute {boolean} videoMuted
 *   Value based on whether or not current user's video is muted or not
 * @attribute {function} toggleVideo
 *   Makes toggleVideo() publicly accessible
 */
conferenceClient.controller('conferenceCtrl', [
    '$rootScope',
    '$q',
    '$scope',
    '$timeout',
    '$interval',
    '$window',
    'logService',
    'socketService',
    'connectivityService',
    'vertoService',
    'attendeeService',
    'storageService',
    'helperService',
    'meetingService',
    'notificationService',
    'analyticsWrapperService',
    'contentService',
    'supportLinkService',
    'localStreamManagerService',
    'avDeviceService',
    'localizationService',
    'brandingService',
    function (
        $rootScope,
        $q,
        $scope,
        $timeout,
        $interval,
        $window,
        logService,
        socketService,
        connectivityService,
        vertoService,
        attendeeService,
        storageService,
        helperService,
        meetingService,
        notificationService,
        analyticsWrapperService,
        contentService,
        supportLinkService,
        localStreamManagerService,
        avDeviceService,
        localizationService,
        brandingService
    ) {
        var self = this;

        const logger = logService.createLoggerWithSource('conferenceCtrl');

        self.init = init;

        self.name = attendeeService.getScreenName();
        self.email = attendeeService.getEmail();
        self.attendantId = attendeeService.getAttendantId();
        self.attendantLoginId = attendeeService.getAttendantLoginId();
        self.isViewerOnly = attendeeService.getIsViewerOnly();
        self.isHost = attendeeService.getIsHost();
        self.audioDevice = storageService.get('audio-device');
        self.videoDevice = storageService.get('video-device');
        self.audioOutputDevice = storageService.get('audio-output-device');
        self.isMobile = helperService.isMobile();
        self.deviceCheckDone = false;
        self.startVertoCall = startVertoCall;
        self.disconnectUnrequested = false;
        self.onScopeDestroyed = onScopeDestroyed;

        self.onPermissionUpdated = onPermissionUpdated;
        self.onPermissionRejected = onPermissionRejected;
        self.requestNotificationAPIPermission = requestNotificationAPIPermission;
        self.showConnectedOverlay = showConnectedOverlay;

        //Mobile
        self.attachMobileResignEvent = attachMobileResignEvent;

        self.isReceivingOnly = isReceivingOnly;
        self.initiateVertoCall = initiateVertoCall;
        self.checkIfMutedForAutoplay = checkIfMutedForAutoplay;

        //TODO: This may only be public for testing
        self.onFinishedMediaDeviceSelection = onFinishedMediaDeviceSelection;
        self.startWebrtcPromptOverlay = startWebrtcPromptOverlay;
        self.cancelWebrtcPromptOverlay = cancelWebrtcPromptOverlay;
        self.showBlockedAlert = showBlockedAlert;
        self.showBlockedPermissionsBanner = showBlockedPermissionsBanner;
        self.showDeviceWarningAlert = showDeviceWarningAlert;
        self.finalizeRoundtripConnection = finalizeRoundtripConnection;

        //Checks for Media Device responsiveness
        self.startMediaDeviceCheckTimeout = startMediaDeviceCheckTimeout;
        self.cancelMediaDeviceCheckTimeout = cancelMediaDeviceCheckTimeout;
        self.mediaDeviceCheckTimeout = null;
        self.mediaDeviceCheckTimeoutCount = 0;

        //TODO: This may only be public for testing
        self.determineMicAndCamAvailability = determineMicAndCamAvailability;
        self.hasPreviouslySelectedDevices = hasPreviouslySelectedDevices;

        self.mobileReactionMenuOpen = false;

        $scope.$on('finished-media-device-selection', onFinishedMediaDeviceSelection);

        self.webrtcPermissionPromptOverlay = null;
        self.permissionOverlayShown = false;

        self.callLock = false;
        self.startVertoWait = startVertoWait;

        self.firstInit = false;

        self.askedForNotificationPermission = false;

        self.checkIfAVDevicePermissionsGranted = checkIfAVDevicePermissionsGranted;

        //Device Unplugging / Re-plugging
        self.sendAudioDeviceConnectionStateToServer = sendAudioDeviceConnectionStateToServer;

        $scope.$on('fs-ws-connected', onFsWsConnected);

        $scope.$on('device-permissions-updated', self.onPermissionUpdated);
        $scope.$on('device-permissions-rejected', self.onPermissionRejected);

        $scope.$on('$destroy', self.onScopeDestroyed);
        $scope.$on('attendee-type-updated', onAttendeeTypeUpdated);

        $scope.$on('mobile-reaction-menu-opened', onMobileReactionMenuOpened);
        $scope.$on('mobile-reaction-menu-closed', onMobileReactionMenuClosed);

        //TODO: device permission events ... for overlay stuff
        //      change event names ... just temporary for dev
        $scope.$on('new-explicit-permission-granted', _newPermissionGranted);
        $scope.$on('new-explicit-permission-denied', _newPermissionDenied);

        $scope.$on('catalina-ss-security-error', _onCatalinaSSError);

        $scope.$on('node-reconnected', _newOnNodeReconnected);
        $scope.$on('fs-connection-complete', _newOnFSConnectionComplete);

        $scope.$on('verto-ice-connection-completed', _onVertoCallCompleted);
        $scope.$on('verto-call-error', _onVertoCallError);
        $scope.$on('verto-call-started', _onVertoCallStarted);

        $scope.$on('verto-call-unrequested-disconnect', onVertoCallUnrequestedDisconnect);

        $scope.$on('start-webrtc-prompt-overlay', self.startWebrtcPromptOverlay);
        $scope.$on('initiate-verto-call', self.initiateVertoCall);

        $scope.$on('video-filter-changed', onVideoFilterChanged);

        function onVideoFilterChanged(e, data) {
            logger.log('conferenceCtrl video-filter-changed', data);
            if (!attendeeService.getIsVideoMuted()) {
                attendeeService.toggleVideo();
                restartVideo();
            }
        }

        function restartVideo() {
            if (!attendeeService.getIsWaitingForVideoUpdate()) {
                attendeeService.toggleVideo();
            } else {
                $timeout(restartVideo, 100);
            }
        }

        //our call retries can send this
        //fill in these events as necessary
        function _newPermissionGranted() {
            logger.log('starting _newPermissionGranted ...');
            if (self.permissionOverlayShown) {
                self.permissionOverlayShown = false;
                $window.swal.close();
            }
            self.cancelWebrtcPromptOverlay();
            self.cancelMediaDeviceCheckTimeout();

            //TODO: figure out the chaining for thisd
            //$window.swal.close();

            //Audio Stream Successfully Retrieved
            if (avDeviceService.getDevicePermissions().audioAllowed) {
                self.sendAudioDeviceConnectionStateToServer(true);
            }
        }

        //probably need double check some stuff here
        function _newPermissionDenied() {
            logger.log('starting _newPermissionDenied ...');
            self.cancelWebrtcPromptOverlay();
            $window.swal.close();
            self.cancelMediaDeviceCheckTimeout();
        }

        function _onCatalinaSSError() {
            var title = localizationService.translate('SCREENSHARE.DIALOG.YOUR_MAC_NEEDS_PERMISSION_TO_SCREENSHARE');
            var link = supportLinkService.getSupportLink('catalinaScreenShare');
            var msg = localizationService.translate('SCREENSHARE.DIALOG.CLICK_HERE_TO_SOLVE_THIS_ISSUE', {
                link: link
            });

            $window.swal({
                title: title,
                text: msg,
                type: 'warning',
                showCancelButton: false,
                showConfirmButton: true,
                confirmButtonText: localizationService.translate('COMMON.OK_TEXT'),
                allowOutsideClick: false,
                html: true
            });
        }

        function _newOnNodeReconnected() {}

        //TODO: Probably should check browser/device ... track if already granted ... etc/etc
        //      may not even be a good idea
        function _onVertoCallStarted() {
            logger.log('calling _onVertoCallStarted ...');

            self.checkIfAVDevicePermissionsGranted()
                .then(function () {
                    var devicePermissions = avDeviceService.getDevicePermissions();

                    if (devicePermissions.audioAllowed && devicePermissions.videoAllowed) {
                        //This implies that the user has already allowed their devices to be shared...
                        //We now set a timeout -- if the getUserMedia callback doesn't execute in 3 seconds,
                        //we notify the user that there may be a device issue
                        self.startMediaDeviceCheckTimeout();
                    } else {
                        //This implies that the user has not yet selected to allow/deny their devices
                        //This function call sets a timeout -- if the getUserMedia callback doesn't execute in 3 seconds,
                        //we prompt the user to allow/deny their devices
                        self.startWebrtcPromptOverlay();
                    }
                })
                .catch(function (err) {
                    logger.log('Unable to check if AV Device permissions granted:', err);
                    self.startWebrtcPromptOverlay();
                });
        }

        //when the call completes, we have to ask node if a valid
        //  FS member id exists ... in case of a FS disconnect ... the add member
        //  never fires and you don't get the finalize call
        function _onVertoCallCompleted() {
            logger.log('calling _onVertoCallCompleted ...');

            var message = {
                message: 'fs-connection-complete',
                data: {},
                callback: function (result) {
                    logger.log('result received from fs-connection-complete check', result);
                    if (result) {
                        self.finalizeRoundtripConnection();

                        //This will allow meetingActiveCtrl.onFSConnectionComplete to be called
                        $rootScope.$broadcast('fs-connection-complete-local-check');
                    }
                }
            };

            socketService.send(message);
        }

        //TODO: eventually this transform piece can be merged into log service ... but it's some work to do properly
        function _logErrorWithTransform(err) {
            //this puts the object in the console ... and maybe log entries ... see below
            logger.log('verto error native ', err);

            //our browser needs to support this method to properly check here
            if (!Object.getPrototypeOf) {
                return;
            }

            //if the object is a native object, then it's already logged in log entries, we can exit
            if (Object.getPrototypeOf(err) === Object.prototype) {
                return;
            }

            //Object.assign and a few other newer object methods should work here, however it looks
            //like the webrtc error properties aren't necessarily enumerable ... and I guess we don't care about methods
            //only properties ... however, this won't catch nested objects
            try {
                var transformedError = {};

                for (var k in err) {
                    transformedError[k] = err[k];
                }

                logger.log('verto error transformed ', transformedError);
            } catch (ex) {}
        }

        //Read the last call error and determine if it was a device or permission error
        function _onVertoCallError() {
            logger.log('calling _onVertoCallError ...');

            //not trying to cover the firefox clicked outside of permission dialog
            //    and made it go away test yet

            var err = vertoService.getLastCallError();

            if (!err) {
                _handleDeviceError(err);
                return;
            }

            _logErrorWithTransform(err);

            if (err && err.name) {
                if (
                    err.name === 'SecurityError' ||
                    err.name === 'NotAllowedError' ||
                    err.name === 'PermissionDeniedError' ||
                    (err.name === 'MediaStreamError' && err.message && err.message.indexOf('denied') > -1)
                ) {
                    _handlePermissionError(err);
                    return;
                }
            }

            _handleDeviceError(err);
        }

        function _handlePermissionError(err) {
            logger.error('_handlePermissionError', 'permission error', err);

            self.showBlockedPermissionsBanner();

            analyticsWrapperService.eventTrack('meeting - webrtc mic/webcam permission rejected', {
                category: 'banner message'
            });

            var permissions = avDeviceService.getDevicePermissions();

            var newCallType = 'noMicNoCam';
            if (permissions.audioAllowed && !permissions.videoAllowed) {
                newCallType = 'audioOnly';
            } else if (!permissions.audioAllowed && permissions.videoAllowed) {
                newCallType = 'videoOnly';
            }

            vertoService.retryCall(newCallType, vertoService.getCurrentCaller());

            $timeout(function () {
                self.showBlockedAlert();
            }, 500);
        }

        function _getDeviceErrorType(err) {
            if (!err) {
                return 'generic';
            }

            if (err.message) {
                if (err.message.indexOf('videosource') > -1) {
                    return 'cam';
                }

                if (err.message.indexOf('audiosource') > -1) {
                    return 'mic';
                }
            }

            if (err.name === 'ConstraintNotSatisfiedError' && err.constraintName) {
                if (err.constraintName === 'width' || err.constraintName === 'height') {
                    return 'cam';
                }
            }

            return 'generic';
        }

        function _handleDeviceError(err) {
            logger.error('_handleDeviceError', 'device error', err);

            analyticsWrapperService.eventTrack('meeting - webrtc mic/webcam device issue', {
                category: 'conference'
            });

            var badSource = _getDeviceErrorType(err); //mic, camera, generic

            switch (badSource) {
                case 'cam':
                    avDeviceService.setDeviceErrors(true, false);
                    vertoService.retryCall('audioOnly', vertoService.getCurrentCaller());
                    break;
                case 'mic':
                    avDeviceService.setDeviceErrors(false, true);
                    connectivityService.setAudioThroughPhone(true, true);
                    vertoService.retryCall('videoOnly', vertoService.getCurrentCaller());
                    break;
                default:
                    avDeviceService.setDeviceErrors(true, true);
                    vertoService.retryCall('noMicNoCam', vertoService.getCurrentCaller());
            }

            $timeout(function () {
                self.showDeviceWarningAlert(badSource);
            }, 500);
        }

        function _newOnFSConnectionComplete() {
            logger.log('conferenceCtrl received fs-connection-complete event');

            self.showConnectedOverlay();

            self.finalizeRoundtripConnection();

            if (!self.askedForNotificationPermission && !self.isReceivingOnly()) {
                self.requestNotificationAPIPermission();
                self.askedForNotificationPermission = true;
            }
        }

        //this should get called when the node server triggers the fs-connection-complete event
        //   OR if we get a valid member ID back on the initial connection
        //   as it stands now, don't put anything in this function that couldn't get called twice in an
        //   "echo" event case
        function finalizeRoundtripConnection() {
            //this was previously called
            $rootScope.$broadcast('banner-message-dismiss');

            //don't do this for ios mobile
            if (helperService.getDevicePlatform() !== 'iOS') {
                self.checkIfMutedForAutoplay();
            }

            //related to ANYAM-5880 - putting in a hack here to make sure that the unsupported banner message
            // is shown after a disconnect.  This needs to be changed as part of a better banner message fix.
            //TODO: not sure how we would get here
            if (!helperService.isBrowserVersionSupported()) {
                showBrowserUnsupportedBanner();
            }

            connectivityService.setFSMemberConnected(true);

            if (self.disconnectUnrequested) {
                self.disconnectUnrequested = false;

                if (contentService.hasUnrequestedStop()) {
                    //Content may or may not have stopped during disconnect...
                    //If it did stop, prompt user that they will need to start sharing again
                    if (!contentService.isContentPlaying()) {
                        var stoppedContentType = contentService.getUnrequestedStopType();
                        showUnrequestedContentStoppedAlert(stoppedContentType);
                    }

                    //Clear unrequested stop
                    contentService.setUnrequestedStopType('');
                }

                var reconnectingPairId = attendeeService.getReconnectingPairId();
                if (reconnectingPairId) {
                    socketService.send({
                        message: 'pair-phone-caller',
                        data: {
                            phoneMemberId: reconnectingPairId
                        }
                    });

                    attendeeService.setReconnectingPairId(null);
                }
            }

            if (attendeeService.getIsWaitingForPromoteDemote()) {
                //If client was waiting for promotion/demotion to finish,
                //we use finalizeRoundtripConnection to indicate
                //that the promote/demote state change is complete
                attendeeService.setIsWaitingForPromoteDemote(false);
            }

            if (helperService.isMobile()) {
                var mobilePermissions = vertoService.getMobilePermissions();

                if (!mobilePermissions || (!mobilePermissions.audioAllowed && !mobilePermissions.videoAllowed)) {
                    analyticsWrapperService.eventTrack(
                        'meeting - webrtc mic/webcam permission rejected or device issue',
                        {
                            category: 'banner message'
                        }
                    );

                    $timeout(self.showBlockedAlert, 500);
                }
            }

            //When users are prompted to select an Audio output (computer, phone or no-audio)
            //We track the user choice to make sure Phone-Pair notification tooltip does not get displayed
            //For users who selected 'no-audio'...
            //
            //We reset it here after FS connection complete so that if the client switches to phone,
            //This value does not prevent the Phone-Pair notification from showing
            connectivityService.setInitialAudioChoice(null);

            if (attendeeService.getIsVideoMuted() && localStreamManagerService.isLocalVideoTrackActive()) {
                localStreamManagerService.stopLocalVideoTrack();
            }
        }

        self.init();

        ////////////////////////

        /**
         * @memberof conferenceCtrl
         * @function init
         * @description
         *   PUBLIC:
         *
         *   If MediaStreamTrack is undefined or does not have getSources(), this returns right away.
         *
         *   Otherwise, this checks for the availability of mic+cam, and
         *   makes the appropriate Verto call.
         */

        function onMobileReactionMenuOpened() {
            self.mobileReactionMenuOpen = true;
            digest();
        }

        function onMobileReactionMenuClosed() {
            self.mobileReactionMenuOpen = false;
            digest();
        }

        function init() {
            if (!helperService.getMediaDevices()) {
                return logger.error('init', 'mediaDevices is undefined');
            } else if (!helperService.getMediaDevices().enumerateDevices) {
                return logger.error('init', 'mediaDevices.enumerateDevices is not a function');
            }

            connectivityService.setConnectionType('verto');

            var userData = getUserData();

            vertoService.setCurrentCaller(userData);
        }

        function hasPreviouslySelectedDevices() {
            //using-phone-for-audio will only be set
            //by the device selection modal when a user joins a meeting
            //for the first time or updates audio/video settings
            var usingPhoneForAudio = storageService.get('using-phone-for-audio');
            return usingPhoneForAudio !== null;
        }

        //the event that triggers this essentially means the mic webcam options dialog has closed
        function onFinishedMediaDeviceSelection(e, data) {
            logger.log('onFinishedMediaDeviceSelection...', data);

            if (data && data.usePhoneForAudio !== undefined) {
                logger.log('Setting new audioThroughPhone value:', data.usePhoneForAudio);
                connectivityService.setAudioThroughPhone(data.usePhoneForAudio, true);
            }

            if (data && (data.newVideoInputDeviceSelected || data.newAudioInputDeviceSelected)) {
                if (localStreamManagerService.shouldUseDeprecatedSpecForStreams()) {
                    //Older Chrome & Electron
                    logger.log('New A/V device selected... hanging up and starting new Verto call');
                    vertoService.hangup(true);
                    self.initiateVertoCall();
                }
            } else if (data && data.joinMeeting) {
                logger.log('Joining meeting from initial device selection modal');
                self.initiateVertoCall();
            }
        }

        function determineMicAndCamAvailability() {
            return avDeviceService
                .determineMicAndCamAvailability()
                .then(function () {
                    var hasCamera = avDeviceService.hasCamera();
                    if (!hasCamera && !attendeeService.getIsVideoMuted()) {
                        attendeeService.setIsVideoMuted(true);
                    }
                })
                .catch(function (err) {});
        }

        function initiateVertoCall() {
            logger.log('initiateVertoCall ...');
            logger.log('self.callLock ', self.callLock);
            attendeeService.setBroadcastOption({
                video: avDeviceService.hasCamera(),
                audio: avDeviceService.hasMicrophone()
            });

            self.deviceCheckDone = true;

            logger.log(
                'in initiateVertoCall -  vertoService.isVertoSocketConnected(): ' +
                    vertoService.isVertoSocketConnected()
            );
            logger.log('in initiateVertoCall - deviceCheckDone: ' + self.deviceCheckDone);

            self.firstInit = true;
            self.startVertoWait();
        }

        function startVertoWait() {
            if (!self.callLock) {
                logger.log('Starting Verto Wait interval...');

                self.callLock = $interval(function () {
                    if (!vertoService.isVertoSocketConnected() || !self.deviceCheckDone) {
                        return;
                    }

                    logger.log('Clearing Verto Wait interval and starting Verto call...');
                    $interval.cancel(self.callLock);
                    self.callLock = null;
                    self.startVertoCall();
                }, 100);
            } else {
                logger.log('Verto Wait interval already exists...');
            }
        }

        //this function will check to see if device check is done and if client is connected to freeswitch socket
        //this will return if any of the 2 is false
        function startVertoCall() {
            logger.log(
                'in startVertoCall - vertoService.isVertoSocketConnected(): ' + vertoService.isVertoSocketConnected()
            );
            logger.log('in startVertoCall - deviceCheckDone: ' + self.deviceCheckDone);

            if (!vertoService.isVertoSocketConnected() || !self.deviceCheckDone) {
                return;
            }

            $rootScope.$broadcast('toggled-connect-overlay', {
                message: localizationService.translate('WEBINAR_STATUS_BAR.CHECKING_YOUR_BANDWIDTH'),
                showConnectOverlay: true
            });
            vertoService.initialSpeedTest().then(function () {
                $rootScope.$broadcast('toggled-connect-overlay', {
                    message: localizationService.translate('WEBINAR_STATUS_BAR.CONNECTING_TO_YOUR_MEETING'),
                    showConnectOverlay: true
                });

                var userData = getUserData();

                var hasMicrophone = avDeviceService.hasMicrophone();
                var hasCamera = avDeviceService.hasCamera() && !avDeviceService.shouldIgnoreCamera();

                if (helperService.isMobile()) {
                    var mobilePermissions = vertoService.getMobilePermissions();

                    if (!mobilePermissions || !mobilePermissions.audioAllowed) {
                        hasMicrophone = false;
                    }

                    if (!mobilePermissions || !mobilePermissions.videoAllowed) {
                        hasCamera = false;
                    }
                }

                if (meetingService.isWebinarAttendee()) {
                    logger.log('webinar attendee. will call noMicNoCam');
                    vertoService.callNoMicNoCam(userData);
                } else if (hasMicrophone && hasCamera) {
                    logger.log('Has a microphone and a camera');
                    vertoService.call(userData);
                } else if (hasMicrophone && !hasCamera) {
                    logger.log('Has a microphone, but no camera');
                    vertoService.callAudioOnly(userData);
                } else if (!hasMicrophone) {
                    //If client has no Mic,
                    //Enabled switch-to-phone mode
                    if (!helperService.isMobile()) {
                        connectivityService.setAudioThroughPhone(true, true);
                    }

                    if (hasCamera) {
                        logger.log('Has a camera, but no microphone');
                        vertoService.callVideoOnly(userData);
                    } else {
                        logger.log('Has no microphone and no camera');
                        vertoService.callNoMicNoCam(userData);
                    }
                }
            });
        }

        /**
         * @memberof conferenceCtrl
         * @function getMatchingDevice
         * @description
         *   PRIVATE:
         *
         *   Iterates through media sources and returns the matching device.
         *   Returns null if no matching device found.
         *
         * @param {int} id
         *   Unique ID of the audio or video device
         * @param {object} mediaSources
         *   List of all available media sources
         */
        function getMatchingDevice(id, mediaSources) {
            if (id === null) {
                return null;
            }
            for (var i = 0; i < mediaSources.length; i++) {
                if (mediaSources[i].deviceId === id) {
                    return id;
                }
            }
            return null;
        }

        function onFsWsConnected() {
            //We need this here to make Verto call on FS reconnect
            logger.log('onFsWsConnected ...');
            if (self.firstInit) {
                logger.log('after first initialization ... calling startVertoWait ');

                // checking to see if FS crashed after 2 seconds
                // if we have a dialog, then the existing call reconnected to the prior call correctly
                // otherwise we hang it up and establish a new call
                $timeout(function () {
                    if (vertoService.getCurCall()) {
                        return;
                    }

                    if (
                        meetingService.isWebinar() &&
                        attendeeService.getIsPresenter() &&
                        !self.hasPreviouslySelectedDevices()
                    ) {
                        //Webinar Attendees who get promoted to Presenter have their FS sockets disconnect/reconnect...
                        //
                        //In this case, they will be prompted with the micWebcamOptions modal before joining the conference
                        //so we don't need to call startVertoWait below
                        return;
                    }

                    self.startVertoWait();
                }, 2000);
            }
        }

        //This was originally in meetingService.
        //Moved it here because we want to attach the 'resign' event
        //after user reaches meeting-active view.
        function attachMobileResignEvent() {
            // Just for Cordova apps.
            if (!self.isMobile) {
                return;
            }

            // Just for iOS devices.
            if (helperService.getDevicePlatform() === 'iOS') {
                logger.log("Attaching 'resign' event...");
                $window.document.addEventListener(
                    'resign',
                    function () {
                        meetingService.endMeeting();
                        analyticsWrapperService.eventTrack('meeting - exited meeting app');
                    },
                    false
                );
            }
        }

        //TODO: event seems to only broadcast on hangup from vertoService
        //      and a no mic/no cam call case in the main start call flow
        //      these two cases don't need to broadcast this event per-se
        function onPermissionUpdated() {
            self.cancelWebrtcPromptOverlay();
            $rootScope.$broadcast('banner-message-dismiss');
        }

        //move these to the new explict handlers
        function onPermissionRejected() {
            self.cancelWebrtcPromptOverlay();
        }

        function requestNotificationAPIPermission() {
            helperService.tryRequestDesktopNotificationPermission();
        }

        function onScopeDestroyed() {
            vertoService.hangup(true);
        }

        function onAttendeeTypeUpdated(att, data) {
            if (vertoService.isEdgeServerEnabled()) {
                vertoService.hangup(true);
                vertoService.disconnectFSSocket();
                vertoService
                    .connectFSSocket()
                    .then(self.determineMicAndCamAvailability)
                    .then(function () {
                        if (data && data.newType !== 'attendee' && !self.hasPreviouslySelectedDevices()) {
                            if (helperService.getIsRedesign()) {
                                $rootScope.$broadcast('show-modal', 'meetingSettings');
                            } else {
                                $rootScope.$broadcast('show-modal', 'micWebcamOptions');
                            }
                        } else {
                            attendeeService.setIsAudioMuted(true);
                            self.startVertoWait();
                        }
                    });
            }
        }

        function isReceivingOnly() {
            if (meetingService.isWebinar() && !attendeeService.getIsHost() && !attendeeService.getIsPresenter()) {
                return true;
            }
            return false;
        }

        function showConnectedOverlay() {
            $timeout(function () {
                $rootScope.$broadcast('toggled-connect-overlay', {
                    showConnectOverlay: false
                });
            }, 2000);

            $rootScope.$broadcast('toggled-connect-overlay', {
                message: localizationService.translate('WEBINAR_STATUS_BAR.CONNECTED'),
                showConnectOverlay: true
            });
        }

        function showDeviceWarningAlert(source) {
            var title = 'Device Problem';
            var text =
                '<b>There was a problem with one or more of your devices.  You may not be able to use your microphone or camera during this event.  Restarting your web browser may resolve the problem.</b>';

            switch (source) {
                case 'mic':
                    title = 'Microphone Problem';
                    text =
                        '<b>There was a problem with your Microphone.  You may not be able to use it during this event.  Restarting your web browser may resolve the problem.</b>';
                    break;
                case 'cam':
                    title = 'Webcam Problem';
                    text =
                        '<b>There was a problem with your Webcam.  You may not be able to use it during this event.  Restarting your web browser may resolve the problem.</b>';
                    break;
            }

            $window.swal(
                {
                    title: title,
                    text: text,
                    type: 'warning',
                    showCancelButton: false,
                    showConfirmButton: true,
                    confirmButtonText: 'Ok',
                    confirmButtonColor: brandingService.getBaseColor(),
                    closeOnConfirm: true,
                    allowOutsideClick: false,
                    html: true
                },
                function (confirm) {}
            );
        }

        //TODO: this was moved from verto service
        function showBlockedAlert() {
            avDeviceService.showBlockedAlert();

            //TODOD: probably need to change this event name since it's been moved ... but keeping vertoService for now
            //       to allow tracking from previous event
            analyticsWrapperService.eventTrack('Meeting - vertoService - permission blocked modal shown', {
                category: 'conference permission overlay'
            });
        }

        //TODO: this was moved from verto service
        function cancelWebrtcPromptOverlay() {
            if (self.webrtcPermissionPromptOverlay !== null) {
                $timeout.cancel(self.webrtcPermissionPromptOverlay);
                self.webrtcPermissionPromptOverlay = null;
            }
        }

        //TODO: this was moved from verto service
        function startWebrtcPromptOverlay() {
            //Camaro users won't have an option to allow/block their devices
            //So we won't ever need to show this alert to them
            if (helperService.isElectron()) {
                return;
            }

            self.cancelWebrtcPromptOverlay();
            self.webrtcPermissionPromptOverlay = $timeout(function () {
                $rootScope.$broadcast('change-meeting-content-opacity', true);

                self.permissionOverlayShown = true;
                $window.swal(
                    {
                        title: localizationService.translate('HEADER.DIALOG.SHARE_MIC_AND_WEBCAM'),
                        type: 'warning',
                        showCancelButton: true,
                        showConfirmButton: true,
                        confirmButtonText: localizationService.translate('HEADER.DIALOG.DO_NOT_WANT_TO_SHARE'),
                        cancelButtonText: localizationService.translate('COMMON.NEED_HELP'),
                        allowOutsideClick: false,
                        closeOnCancel: false,
                        closeOnConfirm: false,
                        customClass: 'initial-webrtc',
                        html: true
                    },
                    function (confirm) {
                        if (confirm) {
                            if (!vertoService.isInCall()) {
                                $('#micWebcamOptions').modal('hide');
                            }
                            $rootScope.$broadcast('toggled-connect-overlay', {
                                message: localizationService.translate('WEBINAR_STATUS_BAR.CONNECTING_TO_YOUR_MEETING'),
                                showConnectOverlay: true
                            });
                            self.showBlockedPermissionsBanner();
                            vertoService.hangup(true);
                            vertoService.callNoMicNoCam(vertoService.getCurrentCaller());
                            $timeout(function () {
                                self.showBlockedAlert();
                            }, 500);
                            analyticsWrapperService.eventTrack("Meeting - vertoService - I Don't want to share", {
                                category: 'conference permission overlay'
                            });
                        } else {
                            analyticsWrapperService.eventTrack('Meeting - vertoService - I need help', {
                                category: 'conference permission overlay'
                            });
                            // These two rows does not work anymore, existing changeMicCamBroser link is broken
                            // var tempLink = supportLinkService.getSupportLink('changeMicCamBrowser');
                            // meetingService.openExternalLink(tempLink);
                        }
                    }
                );
                $timeout(function () {
                    if (helperService.isFirefox()) {
                        $window.angular.element('.initial-webrtc .confirm').addClass('visible');
                        $window.angular.element('.initial-webrtc .cancel').addClass('visible');
                    }
                }, 2000);

                analyticsWrapperService.eventTrack('Meeting - vertoService - permission overlay shown', {
                    category: 'conference permission overlay'
                });
            }, 500);
        }

        function showBlockedPermissionsBanner() {
            avDeviceService.showBlockedPermissionsBanner();
        }

        function onVertoCallUnrequestedDisconnect() {
            self.disconnectUnrequested = true;

            if (contentService.isContentPlaying() && contentService.getContentOwner() === self.attendantId) {
                var curContentType = contentService.getContentType();
                contentService.setUnrequestedStopType(curContentType);
            }

            if (vertoService.useEdgeServer() && !helperService.getIsMeetingEnding()) {
                //for webinar attendees on cdn server, if they get an unrequested verto disconnect
                //they will call back in to cdn server again
                //
                //added timeout because there seems to be timing issues
                //when disconnecting socket directly after a call disconnect
                //
                //TODO: add max retry attempts?
                $timeout(function () {
                    vertoService.hangup(true);
                    vertoService.disconnectFSSocket();
                    vertoService.connectFSSocket().then(self.determineMicAndCamAvailability).then(self.startVertoWait);
                    notificationService.show('There seems to be an issue. Reconnecting you to the webinar', 'warning');
                }, 3000);
            }
        }

        //Private function
        function showBrowserUnsupportedBanner() {
            var browser;
            var version;
            var helpURL;

            if (helperService.isChrome()) {
                browser = 'Chrome';
                version = helperService.isMobileRedirectByPass() ? false : helperService.getChromeVersion();
                helpURL = 'https://support.google.com/chrome/answer/95414';
            } else if (helperService.isFirefox()) {
                browser = 'Firefox';
                version = helperService.getFirefoxVersion();
                helpURL = 'https://support.mozilla.org/en-US/kb/update-firefox-latest-version';
            }

            logger.log('Browser Version not supported:', browser, version);

            analyticsWrapperService.eventTrack('meeting - browser version not supported');

            $rootScope.$broadcast('banner-message-show', {
                header: '',
                content: localizationService.translate('WEBINAR_STATUS_BAR.BROWSER_NOT_SUPPORTED.CONTENT', {
                    helpURL: helpURL
                }),
                persist: false
            });
        }

        function getInitialIsAudioMuted() {
            if (self.isHost) {
                return false;
            }

            if (meetingService.getMeetingType() === 'meeting') {
                if (!meetingService.getJoinAudioMuted() && !attendeeService.getIsAudioMuted()) {
                    //if meeting does not enforce join muted and the user is not muted Also
                    //look at localstorage to determine the audio mute behavior when joining
                    var joinAudioStatePref = storageService.get('joinAudioStatePref');
                    if (joinAudioStatePref !== null) {
                        return joinAudioStatePref;
                    } else {
                        return true;
                    }
                }
            }

            if (meetingService.getMeetingType() === 'webinar') {
                if (attendeeService.getIsPresenter()) {
                    return true;
                }
            }

            return attendeeService.getIsAudioMuted();
        }

        function getUserData() {
            var userData = {
                name: self.name,
                callerID: self.email,
                attendantLoginId: self.attendantLoginId,
                isViewerOnly: self.isViewerOnly,
                isHost: self.isHost,
                isVideoMuted: attendeeService.getIsVideoMuted(),
                isAudioMuted: getInitialIsAudioMuted(),
                isPresenter: attendeeService.getIsPresenter()
            };

            return userData;
        }

        function digest() {
            if (!$scope.$$phase) {
                $scope.$digest();
            }
        }

        //Private Function
        function showUnrequestedContentStoppedAlert(stoppedContentType) {
            var msgTitle = '';
            var msgTxt = '';

            if (stoppedContentType === 'slides' || stoppedContentType === 'video') {
                msgTitle = localizationService.translate('SCREENSHARE.DIALOG.CONTENT_SHARING_ENDED_UNEXPECTEDLY.TITLE');
                msgTxt = localizationService.translate('SCREENSHARE.DIALOG.CONTENT_SHARING_ENDED_UNEXPECTEDLY.TEXT');
            } else if (stoppedContentType === 'screenshare') {
                msgTitle = localizationService.translate('SCREENSHARE.DIALOG.SCREEN_ENDED_UNEXPECTEDLY.TITLE');
                msgTxt = localizationService.translate('SCREENSHARE.DIALOG.SCREEN_ENDED_UNEXPECTEDLY.TEXT');
            }

            $window.swal({
                title: msgTitle,
                text: msgTxt,
                type: 'warning',
                showCancelButton: false,
                showConfirmButton: true,
                confirmButtonColor: brandingService.getBaseColor(),
                confirmButtonText: localizationService.translate('COMMON.OKAY_TEXT'),
                allowOutsideClick: true
            });
        }

        function checkIfMutedForAutoplay() {
            var curCall = vertoService.getCurCall();
            if (curCall && curCall.rtc && curCall.rtc.options && curCall.rtc.options.mutedForAutoplay) {
                logger.log('Media feed muted for autoplay... prompting user to unmute feed');

                $window.swal(
                    {
                        title: localizationService.translate('LOBBY_PAGE.AUTOMATICALLY_MUTED.TITLE'),
                        showCancelButton: false,
                        showConfirmButton: true,
                        confirmButtonText: localizationService.translate('COMMON.OK_TEXT'),
                        confirmButtonColor: brandingService.getBaseColor(),
                        closeOnConfirm: true,
                        allowOutsideClick: false
                    },
                    function () {
                        logger.log('Unmuting media feed');
                        $window.$('#videoFeed').prop('muted', false);
                    }
                );
            }
        }

        function checkIfAVDevicePermissionsGranted() {
            return avDeviceService.checkIfAVDevicePermissionsGranted();
        }

        function startMediaDeviceCheckTimeout() {
            if (!self.mediaDeviceCheckTimeout) {
                logger.log('Starting Media Device Check Timeout');

                self.mediaDeviceCheckTimeout = $timeout(function () {
                    analyticsWrapperService.eventTrack('Meeting - Media Device Check - getUserMedia Timed Out');

                    vertoService.abortCurrentGetUserMediaRequest();

                    self.mediaDeviceCheckTimeout = null;
                    self.mediaDeviceCheckTimeoutCount++;

                    if (self.mediaDeviceCheckTimeoutCount <= 3) {
                        logger.log(
                            'Media Device Check Timed Out - Count: ' +
                                self.mediaDeviceCheckTimeoutCount +
                                ' - Retrying call with ' +
                                vertoService.getCurrentCallType()
                        );
                        vertoService.retryCall(vertoService.getCurrentCallType(), vertoService.getCurrentCaller());
                    } else {
                        logger.log('Media Device Check Timed Out - Max Tries Reached - Retrying call with noMicNoCam');
                        vertoService.retryCall('noMicNoCam', vertoService.getCurrentCaller());

                        var supportLink = supportLinkService.getSupportLink('manageDevices');
                        var title = localizationService.translate(
                            'HEADER.PERMISSIONS_ERROR.COMMON.PROBLEM_ACCESSING_DEVICE_HEADER'
                        );
                        var msg = localizationService.translate(
                            'HEADER.PERMISSIONS_ERROR.COMMON.PROBLEM_ACCESSING_DEVICE',
                            {
                                link: supportLink
                            }
                        );

                        $window.swal({
                            title: title,
                            text: msg,
                            type: 'warning',
                            showCancelButton: false,
                            showConfirmButton: true,
                            confirmButtonText: localizationService.translate('COMMON.OK_TEXT'),
                            allowOutsideClick: false,
                            html: true
                        });

                        analyticsWrapperService.eventTrack('Meeting - Media Device Check - Modal Shown');
                    }
                }, 10000);
            }
        }

        function cancelMediaDeviceCheckTimeout() {
            if (self.mediaDeviceCheckTimeout) {
                logger.log('Cancelling Media Device Check Timeout');
                $timeout.cancel(self.mediaDeviceCheckTimeout);
                self.mediaDeviceCheckTimeout = null;
                self.mediaDeviceCheckTimeoutCount = 0;
            }
        }

        function sendAudioDeviceConnectionStateToServer(audioConnected) {
            socketService.send({
                message: 'attendee-device-connections-updated',
                data: {
                    deviceConnections: {
                        audioConnected: audioConnected
                    }
                }
            });
        }
    }
]);
